import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@helpers/translate";
import bgHand from "@assets/img/bg-hand.png";
import accountIcon from "@assets/img/account-icon.svg";
import walletIcon from "@assets/img/wallet-icon.svg";
import statsIcon from "@assets/img/stats-icon.svg";
import "./StillThinking.sass";

const InfoCard = ({ number, title, description }) => (
  <div className="info-card">
    <span className="info-card__number text-iron">{number}</span>

    <h3 className="info-card__title text-shark">
      {title}
    </h3>

    <p className="info-card__description text-storm-gray">
      {description}
    </p>
  </div>
);

const MobileInfoCard = ({
  number,
  title,
  description,
  icon,
}) => {
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const cardElement = cardRef.current;

    if (!cardElement || hasBeenViewed) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isCardVisible =
          entry.isIntersecting &&
          entry.intersectionRatio >= 0.5;

        if (isCardVisible) {
          setHasBeenViewed(true);

          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: "-30% 0px -30% 0px",
        threshold: [0, 0.3, 0.5, 0.7, 1],
      }
    );

    observer.observe(cardElement);

    return () => {
      observer.disconnect();
    };
  }, [hasBeenViewed]);

  return (
    <div
      ref={cardRef}
      className={`mobile-info-card ${
        hasBeenViewed
          ? "mobile-info-card--active"
          : "mobile-info-card--inactive"
      }`}
    >
      <div className="mobile-info-card__icon-wrapper">
        <img
          src={icon}
          alt={title}
          className="mobile-info-card__icon"
        />
      </div>

      <div className="mobile-info-card__content">
        <span className="mobile-info-card__number text-iron">
          {number}
        </span>

        <p className="mobile-info-card__title text-shark">
          {title}
        </p>

        <p className="mobile-info-card__description text-storm-gray">
          {description}
        </p>
      </div>
    </div>
  );
};

const StillThinking = () => {
  const { __ } = useTranslation();

  const cardData = [
    {
      id: 0,
      number: "01",
      title: __("main.stillThinking.step1Title"),
      description: __("main.stillThinking.step1Desc"),
      icon: accountIcon,
    },
    {
      id: 1,
      number: "02",
      title: __("main.stillThinking.step2Title"),
      description: __("main.stillThinking.step2Desc"),
      icon: walletIcon,
    },
    {
      id: 2,
      number: "03",
      title: __("main.stillThinking.step3Title"),
      description: __("main.stillThinking.step3Desc"),
      icon: statsIcon,
    },
  ];

  return (
    <section className="still-thinking">
      <div className="still-thinking__gradient-top" />
      <div className="still-thinking__gradient-left" />
      <div className="still-thinking__gradient-right" />

      <div className="still-thinking__mobile">
        <div className="still-thinking__mobile-header">
          <h1 className="still-thinking__mobile-title text-shark">
            {__("main.stillThinking.title")}
          </h1>

          <p className="still-thinking__mobile-subtitle text-storm-gray">
            {__("main.stillThinking.subtitle")}
          </p>
        </div>

        <div className="still-thinking__mobile-cards">
          {cardData.map((card) => (
            <MobileInfoCard
              key={card.id}
              number={card.number}
              title={card.title}
              description={card.description}
              icon={card.icon}
            />
          ))}
        </div>
      </div>

      <div className="still-thinking__desktop">
        <img
          src={bgHand}
          alt="Binify on Phone"
          className="still-thinking__desktop-image"
        />

        <div className="still-thinking__desktop-overlay">
          <div className="still-thinking__desktop-left">
            <InfoCard
              number="01"
              title={__("main.stillThinking.step1Title")}
              description={__("main.stillThinking.step1Desc")}
            />
          </div>

          <div className="still-thinking__desktop-right">
            <div>
              <h2 className="still-thinking__desktop-title text-shark">
                {__("main.stillThinking.title")}
              </h2>

              <p className="still-thinking__desktop-subtitle text-storm-gray">
                {__("main.stillThinking.subtitle")}
              </p>
            </div>

            <InfoCard
              number="02"
              title={__("main.stillThinking.step2Title")}
              description={__("main.stillThinking.step2Desc")}
            />
          </div>

          <div className="still-thinking__desktop-card-03">
            <InfoCard
              number="03"
              title={__("main.stillThinking.step3Title")}
              description={__("main.stillThinking.step3Desc")}
            />
          </div>
        </div>
      </div>

      <div className="still-thinking__gradient-bottom" />
    </section>
  );
};

export default StillThinking;
